import React from "react";
import {
  TbClipboardPlus, TbPuzzle, TbTargetArrow, TbRadar, TbCalendarPlus, TbRefresh,
  TbShieldHalf, TbUserPlus, TbLink, TbMail, TbClipboardCheck, TbArrowDownRight, TbArrowUpRight,
  TbChevronRight, TbAlertTriangle, TbProgressAlert, TbCalendarDue, TbCircleCheck, TbArrowRight,
  TbSparkles, TbChartLine, TbUsers, TbLock, TbSpeakerphone, TbSchool, TbExternalLink,
} from "react-icons/tb";
import { useDashboard } from "../../hooks/useDashboard";
import { usePlan } from "../../lib/plan";
import { lockForScreen } from "../../shell/nav";
import { Button, Alert, TopbarSlot } from "../../components";
import { fmtDate, fmtRelative } from "../../lib/dates";
import { riskLevelFor } from "../../lib/prospect";
import { STATE_LABEL } from "../../lib/ratings";
import { latestWhatsNew } from "../../lib/whatsNew";
import "./Dashboard.css";

// The home screen: what came in this week, what it is worth, what needs a hand, and
// what happened recently. Everything is a count or a short list that links to the screen
// where the work is actually done — this page answers "what should I pick up", it does
// not try to be the place you pick it up.
//
// Links to Pro-only screens (the scanner, report templates) are dropped for community
// tenants, matching the rail. A tile that opens a screen you cannot see is worse than a
// tile that opens nothing.

// Ordered as the journey an MSP takes a client through: attract them, scan them before
// the call, map their network once they sign, then review and keep reviewing. `intent`
// opens the target screen's New dialog where it has one (see shell/intent.js).
const QUICK_ACTIONS = [
  { key: "lead-magnet",     label: "Create lead magnet",  desc: "Embed a scan on your site",       icon: TbPuzzle, tone: "violet", intent: { type: "new" } },
  { key: "prospecting",     label: "Run prospect scan",   desc: "Scan a domain before a call",     icon: TbTargetArrow, tone: "warning" },
  { key: "organizations",   label: "Discovery scanning",  desc: "Map a client's network",          icon: TbRadar, tone: "success" },
  { key: "assessments",     label: "Start review",        desc: "Baseline, proposal or review",    icon: TbClipboardPlus, tone: "accent", intent: { type: "new" } },
  { key: "review-schedule", label: "Schedule review",     desc: "Set a client's QBR cadence",      icon: TbCalendarPlus, tone: "danger" },
];

// Screens that need Pro. A link into one is dropped for community tenants.

const ACTIVITY_ICON = {
  scan: TbShieldHalf, lead: TbUserPlus, link: TbLink, report: TbMail, review: TbClipboardCheck,
};

const known = (v) => v !== null && v !== undefined;

const scoreTone = (score) => (!known(score) ? "" : score < 40 ? "danger" : score < 60 ? "warning" : "success");

const Score = ({ value }) =>
  known(value) ? (
    <span className={`v2-dash-score ${scoreTone(value)}`} title={`Score ${value} · ${riskLevelFor(value)} risk`}>{value}</span>
  ) : null;

const Mark = ({ name }) => (
  <span className="v2-dash-mark" aria-hidden="true">{(name || "?").slice(0, 1).toUpperCase()}</span>
);

const Count = ({ n }) => (n ? <span className="v2-dash-count">{n}</span> : null);

const Card = ({ title, icon: Icon, meta, action, className = "", tour, children }) => (
  <section className={`v2-dash-card ${className}`.trim()} data-tour={tour}>
    <header className="v2-dash-card-head">
      {Icon && <Icon size={15} className="v2-dash-card-icon" />}
      <h3>{title}</h3>
      {meta && <span className="v2-dash-card-meta">{meta}</span>}
      {action && <span className="v2-dash-card-action">{action}</span>}
    </header>
    {children}
  </section>
);

const LinkButton = ({ onClick, children }) =>
  onClick ? (
    <button type="button" className="v2-dash-link" onClick={onClick}>
      {children}<TbArrowRight size={13} />
    </button>
  ) : null;

// A stat inside a KPI card. The label is held to one line so every value in a row sits
// at the same height; the full text is on the title for when it is truncated.
//
// A null value means the server could not compute it on this database. It is shown as a
// quiet dash with an explanation, not as a zero and not as a big bold "—".
const Stat = ({ label, value, sub, tone, onClick }) => {
  const body = (
    <>
      <span className="v2-dash-stat-label" title={label}>{label}</span>
      {known(value)
        ? <span className={`v2-dash-stat-value${tone && value ? ` ${tone}` : ""}`}>{value}</span>
        : <span className="v2-dash-stat-value none" title="Not tracked on this server yet">—</span>}
      <span className="v2-dash-stat-sub">{sub}</span>
    </>
  );
  return onClick
    ? <button type="button" className="v2-dash-stat is-link" onClick={onClick}>{body}</button>
    : <div className="v2-dash-stat">{body}</div>;
};

// One line of the attention list. Quiet and ticked when the count is zero, tinted when
// it is not, so the eye goes straight to whatever actually needs doing.
const AttentionRow = ({ icon: Icon, label, desc, count, tone, onClick }) => {
  const clear = known(count) && count === 0;
  const state = !known(count) ? "unknown" : clear ? "clear" : tone;
  return (
    <button type="button" className={`v2-dash-attn ${state}`} onClick={onClick}>
      <span className="v2-dash-attn-icon">{clear ? <TbCircleCheck size={16} /> : <Icon size={16} />}</span>
      <span className="v2-dash-attn-text">
        <span className="v2-dash-attn-label">{label}</span>
        <span className="v2-dash-attn-desc">{clear ? "All clear" : desc}</span>
      </span>
      <span className="v2-dash-attn-count">{known(count) ? count : "—"}</span>
      <TbChevronRight size={15} className="v2-dash-chev" />
    </button>
  );
};

// A list row. A real button when it leads somewhere, so it is reachable from the keyboard.
const Row = ({ onClick, children }) => (
  <li>
    {onClick
      ? <button type="button" className="v2-dash-row is-link" onClick={onClick}>{children}</button>
      : <div className="v2-dash-row">{children}</div>}
  </li>
);

const Empty = ({ children }) => <p className="v2-dash-empty">{children}</p>;

const DashboardScreen = ({ me, onNavigate }) => {
  const { data, loading, error, reload } = useDashboard();

  const go = (key, intent) => onNavigate && onNavigate(key, undefined, intent);
  // Every tile links, locked or not: a locked screen opens on its upgrade page, which is
  // a better answer to "what is this?" than a tile that silently does nothing.
  const to = (key) => () => go(key);
  // Locks come from the same place the nav's do (nav.js lockForScreen + the plan).
  const plan = usePlan();
  const isLocked = (key) => plan.locked(lockForScreen(key));

  const rawFirst = (me?.name || "").split(/[\s@.]/)[0];
  const firstName = rawFirst ? rawFirst[0].toUpperCase() + rawFirst.slice(1) : "";

  const leads = data?.leads || {};
  const pipeline = data?.pipeline || {};
  const attention = data?.attention || {};
  const clients = data?.clients || {};
  const activity = data?.activity || [];

  const reviewsDue = known(attention.reviews_overdue)
    ? attention.reviews_overdue + (attention.reviews_due || 0)
    : null;

  // The one-line briefing under the greeting: the things worth acting on, most urgent
  // first, and nothing at all when there is nothing.
  const briefing = [
    attention.reviews_overdue && `${attention.reviews_overdue} review${attention.reviews_overdue === 1 ? "" : "s"} overdue`,
    attention.high_risk_count && `${attention.high_risk_count} high-risk scan${attention.high_risk_count === 1 ? "" : "s"}`,
    leads.needs_follow_up && `${leads.needs_follow_up} prospect${leads.needs_follow_up === 1 ? "" : "s"} to follow up`,
    clients.to_finalize_count && `${clients.to_finalize_count} review${clients.to_finalize_count === 1 ? "" : "s"} to finalize`,
  ].filter(Boolean);

  // All shown; the ones the plan does not include carry a lock instead of the arrow.
  const actions = QUICK_ACTIONS;

  return (
    <div className="v2-page v2-dash">
      <TopbarSlot>
        <Button icon title="Refresh" aria-label="Refresh the dashboard" onClick={reload} disabled={loading}>
          <TbRefresh size={15} className={loading ? "v2-dash-spin" : ""} />
        </Button>
      </TopbarSlot>

      <header className="v2-dash-hero">
        <div>
          <div className="v2-dash-eyebrow">
            {me?.tenant_name || "Workspace"}{data?.today && <> · {fmtDate(data.today)}</>}
          </div>
          <h2>{firstName ? `Welcome back, ${firstName}` : "Dashboard"}</h2>
          {data && (
            <p className="v2-dash-brief">
              {briefing.length
                ? <><TbSparkles size={14} /> Today: {briefing.join(" · ")}.</>
                : <><TbCircleCheck size={14} /> Nothing needs your attention right now.</>}
            </p>
          )}
        </div>
      </header>

      {/* Quick actions first: someone who already knows what they came to do should not
          have to read past the numbers to get to it. */}
      <nav className="v2-dash-actions" aria-label="Quick actions" data-tour="quick-actions" style={{ "--n": actions.length }}>
        {actions.map(({ key, label, desc, icon: Icon, tone, intent }) => (
          <button key={key} type="button" className={`v2-dash-action ${tone}`} onClick={() => go(key, intent)}>
            <span className="v2-dash-action-icon"><Icon size={18} /></span>
            <span className="v2-dash-action-text">
              <span className="v2-dash-action-label">{label}</span>
              <span className="v2-dash-action-desc">{desc}</span>
            </span>
            {isLocked(key)
              ? <TbLock size={14} className="v2-dash-action-arrow" aria-label="Not in your plan" />
              : <TbArrowRight size={15} className="v2-dash-action-arrow" />}
          </button>
        ))}
      </nav>

      <Alert>{error}</Alert>

      {loading && !data ? (
        <div className="v2-dash-skeleton" aria-busy="true" aria-label="Loading the dashboard">
          <span /><span /><span />
        </div>
      ) : data && (
        <>
          <div className="v2-dash-kpis">
            <Card title="New leads" icon={TbUsers} meta="This week">
              <div className="v2-dash-stats cols-3">
                <Stat label="Lead magnet" value={leads.lead_magnet_submissions}
                      sub="widget submissions" onClick={to("lead-magnet")} />
                <Stat label="Prospect scans" value={leads.prospect_scans}
                      sub="completed" onClick={to("prospecting")} />
                <Stat label="Follow-up" value={leads.needs_follow_up} tone="warning"
                      sub="report not sent yet" onClick={to("prospecting")} />
              </div>
            </Card>

            <Card title="Pipeline impact" icon={TbChartLine} meta="Last 30 days">
              <div className="v2-dash-stats cols-4">
                <Stat label="Prospects scanned" value={pipeline.prospects_scanned}
                      sub="scans and captures" onClick={to("prospecting")} />
                <Stat label="Sales assessments" value={pipeline.sales_assessments}
                      sub="in progress" onClick={to("assessments")} />
                <Stat label="Reports to send" value={pipeline.reports_ready}
                      sub="ready, not yet sent" onClick={to("assessments")} />
                <Stat label="Opportunities" value={pipeline.opportunities} tone="accent"
                      sub="est. · High or Critical" onClick={to("prospecting")} />
              </div>
            </Card>
          </div>

          <div className="v2-dash-grid">
            <div className="v2-dash-main">
              <Card title="Attention needed" icon={TbAlertTriangle} tour="attention"
                    action={<LinkButton onClick={to("reviews-scans")}>Latest scans</LinkButton>}>
                <div className="v2-dash-attn-list">
                  <AttentionRow icon={TbAlertTriangle} tone="danger" count={attention.high_risk_count}
                                label="High-risk findings" desc="Client scans below 60 · 30 days"
                                onClick={() => go("reviews-scans")} />
                  <AttentionRow icon={TbProgressAlert} tone="warning" count={attention.incomplete}
                                label="Failed or incomplete scans" desc="Unscored or abandoned"
                                onClick={() => go("organizations")} />
                  <AttentionRow icon={TbCalendarDue} tone={attention.reviews_overdue ? "danger" : "warning"}
                                count={reviewsDue} label="Reviews due"
                                desc={attention.reviews_overdue
                                  ? `${attention.reviews_overdue} overdue · ${attention.reviews_due || 0} this week`
                                  : "Next 7 days"}
                                onClick={() => go("review-schedule")} />
                </div>

                {(attention.high_risk || []).length > 0 && (
                  <div className="v2-dash-block">
                    <div className="v2-dash-subhead">Lowest scoring client scans</div>
                    <ul className="v2-dash-list">
                      {attention.high_risk.map((r) => (
                        <Row key={r.scan_id}>
                          <Mark name={r.organization_name} />
                          <div className="v2-dash-grow">
                            <div className="v2-dash-row-title">{r.organization_name}</div>
                            <div className="v2-dash-row-sub">{r.domain} · {fmtRelative(r.created_at)}</div>
                          </div>
                          <Score value={r.score} />
                        </Row>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>

              <Card title="Client success" icon={TbClipboardCheck}
                    action={<LinkButton onClick={() => go("review-schedule")}>Review schedule</LinkButton>}>
                <div className="v2-dash-split">
                  <div className="v2-dash-block">
                    <div className="v2-dash-subhead">Upcoming business reviews <Count n={(clients.upcoming || []).length} /></div>
                    {(clients.upcoming || []).length === 0 ? (
                      <Empty>Nothing due in the next 30 days.</Empty>
                    ) : (
                      <ul className="v2-dash-list">
                        {clients.upcoming.map((u) => (
                          <Row key={u.organization_id} onClick={() => go("review-schedule")}>
                            <span className={`v2-dash-date${u.overdue ? " overdue" : ""}`}>
                              <b>{fmtDate(u.next_review).slice(0, 2)}</b>
                              <span>{fmtDate(u.next_review).slice(3, 6)}</span>
                            </span>
                            <div className="v2-dash-grow">
                              <div className="v2-dash-row-title">{u.organization_name}</div>
                              <div className={`v2-dash-row-sub${u.overdue ? " danger" : ""}`}>
                                {u.overdue ? `Overdue since ${fmtDate(u.next_review)}` : fmtRelative(u.next_review)}
                              </div>
                            </div>
                          </Row>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="v2-dash-block">
                    <div className="v2-dash-subhead">Waiting to be finalized <Count n={clients.to_finalize_count} /></div>
                    {(clients.to_finalize || []).length === 0 ? (
                      <Empty>Every business review has been presented.</Empty>
                    ) : (
                      <ul className="v2-dash-list">
                        {clients.to_finalize.map((a) => (
                          <Row key={a.id} onClick={() => go("assessments")}>
                            <Mark name={a.organization_name} />
                            <div className="v2-dash-grow">
                              <div className="v2-dash-row-title">{a.title}</div>
                              <div className="v2-dash-row-sub">{a.organization_name || "Unknown organization"}</div>
                            </div>
                            <span className={`v2-dash-state ${a.state}`}>{STATE_LABEL[a.state] || a.state}</span>
                          </Row>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="v2-dash-block v2-dash-divided">
                  <div className="v2-dash-subhead">Risk changes since last scan <Count n={clients.risk_changes_count} /></div>
                  {(clients.risk_changes || []).length === 0 ? (
                    <Empty>No client's score has moved 10 points or more between its last two scans.</Empty>
                  ) : (
                    <ul className="v2-dash-list">
                      {clients.risk_changes.map((r) => {
                        const worse = r.change < 0;
                        const Arrow = worse ? TbArrowDownRight : TbArrowUpRight;
                        return (
                          <Row key={r.organization_id}>
                            <Mark name={r.organization_name} />
                            <div className="v2-dash-grow">
                              <div className="v2-dash-row-title">{r.organization_name}</div>
                              <div className="v2-dash-row-sub">{r.domain} · {fmtRelative(r.scanned_at)}</div>
                            </div>
                            <span className="v2-dash-change">
                              <span className="v2-dash-prev">{r.previous_score}</span>
                              <TbArrowRight size={12} />
                              <Score value={r.score} />
                            </span>
                            <span className={`v2-dash-delta ${worse ? "danger" : "success"}`}>
                              <Arrow size={13} />{Math.abs(r.change)}
                            </span>
                          </Row>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </Card>
            </div>

            <div className="v2-dash-side">
            <Card title="What's new in Pipsqueak" icon={TbSpeakerphone} tour="whats-new">
              <ul className="v2-dash-list">
                {latestWhatsNew(5).map((n) => (
                  <Row key={`${n.date}-${n.title}`} onClick={n.screen ? to(n.screen) : undefined}>
                    <div className="v2-dash-grow">
                      <div className="v2-dash-row-title">{n.title}</div>
                      <div className="v2-dash-new-desc">{n.desc}</div>
                    </div>
                    <span className="v2-dash-new-date">{fmtDate(n.date)}</span>
                  </Row>
                ))}
              </ul>
            </Card>

            {/* MSP Skool, the free community Pipsqueak is part of. A link out, not a
                feature: kept small and quiet so it sits beside the work rather than
                competing with it, and clearly marked as going to another site. */}
            <a className="v2-dash-promo" href="https://mspskool.com" target="_blank" rel="noopener noreferrer">
              <span className="v2-dash-promo-icon" aria-hidden="true"><TbSchool size={20} /></span>
              <span className="v2-dash-promo-text">
                <span className="v2-dash-promo-eyebrow">Free MSP community</span>
                <span className="v2-dash-promo-title">MSP Skool</span>
                <span className="v2-dash-promo-desc">
                  Built by MSP owners, for MSP owners. Training in leadership, sales, marketing and AI — and peers who get it.
                </span>
                <span className="v2-dash-promo-cta">
                  Join free at MSPSkool.com <TbExternalLink size={13} aria-hidden="true" />
                  <span className="v2-dash-sr"> (opens in a new tab)</span>
                </span>
              </span>
            </a>

            <Card title="Recent activity" icon={TbChartLine} className="v2-dash-activity-card">
              {activity.length === 0 ? (
                <Empty>Scans, lead captures, shared links and reviews will show up here.</Empty>
              ) : (
                <ol className="v2-dash-timeline">
                  {activity.map((a, i) => {
                    const Icon = ACTIVITY_ICON[a.kind] || TbShieldHalf;
                    return (
                      <li key={`${a.kind}-${a.at}-${i}`}>
                        <span className={`v2-dash-kind ${a.kind}`}><Icon size={13} /></span>
                        <div className="v2-dash-grow">
                          <div className="v2-dash-row-title">{a.title}</div>
                          <div className="v2-dash-row-sub">
                            {a.detail && <span className="v2-dash-detail">{a.detail}</span>}
                            <span className="v2-dash-when">{fmtRelative(a.at)}</span>
                          </div>
                        </div>
                        <Score value={a.score} />
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardScreen;
